// Athena Digital Check-in API helpers
// Uses appointmentId from the URL to fetch and update data.

import axiosInstance from '../../utils/axiosConfig';

async function jsonRequest<T>(path: string, options: RequestInit): Promise<T> {
  try {
    let response;
    const method = (options.method || 'GET').toUpperCase();
    
    // Convert headers to plain object if needed
    let headersObj: Record<string, string> = {
      "Content-Type": "application/json",
      client: 'axis',
      domain: 'axis',
    };
    
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headersObj[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        // Handle array of [key, value] pairs
        options.headers.forEach(([key, value]) => {
          headersObj[key] = value;
        });
      } else {
        // Plain object
        headersObj = { ...headersObj, ...options.headers as Record<string, string> };
      }
    }
    
    const config = { headers: headersObj };

    if (method === 'GET') {
      response = await axiosInstance.get(path, config);
    } else if (method === 'PUT') {
      const body = options.body ? JSON.parse(options.body as string) : undefined;
      response = await axiosInstance.put(path, body, config);
    } else if (method === 'POST') {
      const body = options.body ? JSON.parse(options.body as string) : undefined;
      response = await axiosInstance.post(path, body, config);
    } else {
      throw new Error(`Unsupported method: ${method}`);
    }

    if (response.status === 204) {
      // No content
      return undefined as unknown as T;
    }

    return response.data as T;
  } catch (error: any) {
    if (error.response) {
      const text = typeof error.response.data === 'string' 
        ? error.response.data 
        : JSON.stringify(error.response.data);
      throw new Error(text || error.response.statusText || "Request failed");
    }
    throw error;
  }
}

export interface ContactInfo {
  email?: string | null;
  mobilePhone?: string | null;
  homePhone?: string | null;
  workPhone?: string | null;
}

export interface AddressInfo {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  countryCode?: string | null;
}

export interface BillingInfo {
  guarantorFirstName?: string | null;
  guarantorLastName?: string | null;
  guarantorMiddleName?: string | null;
  guarantorSuffix?: string | null;
  guarantorDateOfBirth?: string | null;
  guarantorRelationshipToPatient?: string | null | number;
  guarantorAddress1?: string | null;
  guarantorAddress2?: string | null;
  guarantorCity?: string | null;
  guarantorState?: string | null;
  guarantorZip?: string | null;
  guarantorCountryCode?: string | null;
  guarantorCountryCode3166?: string | null;
  guarantorEmail?: string | null;
  guarantorPhone?: string | null;
  guarantorAddressSameAsPatient?: boolean;
}

export interface EmergencyContact {
  name?: string | null;
  homePhone?: string | null;
  mobilePhone?: string | null;
  relationship?: string | null;
  contactPreference?: string | null;
}

export interface CommunicationPreference {
  announcementEmail?: boolean;
  announcementPhone?: boolean;
  announcementSms?: boolean;
  appointmentEmail?: boolean;
  appointmentPhone?: boolean;
  appointmentSms?: boolean;
  billingEmail?: boolean;
  billingPhone?: boolean;
  billingSms?: boolean;
  labEmail?: boolean;
  labPhone?: boolean;
  labSms?: boolean;
}

export interface DriverLicenseInfo {
  hasDriverLicense?: boolean;
  driverLicenseNumber?: string | null;
  driverLicenseState?: string | null;
  driverLicenseExpirationDate?: string | null;
}

export interface PrimaryProvider {
  providerId?: number;
  displayName?: string;
  schedulingName?: string;
  firstName?: string;
  lastName?: string;
  specialty?: string;
  npi?: string;
}

export interface DemographicsResponse {
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  suffix?: string | null;
  dateOfBirth?: string;
  sex?: string;
  maritalStatus?: string;
  occupation?: string | number | null;
  address?: AddressInfo;
  driverLicense?: DriverLicenseInfo;
  contactInfo?: ContactInfo;
  billingInfo?: BillingInfo;
  emergencyContact?: EmergencyContact;
  language?: string;
  ethnicity?: unknown;
  race?: unknown;
  assignedSexAtBirth?: string;
  genderIdentity?: unknown;
  preferredPronouns?: string;
  sexualOrientation?: string;
  firstNameUsed?: string;
  communicationPreference?: CommunicationPreference;
  primaryProvider?: PrimaryProvider;
  insurances?: unknown[];
}

export interface DemographicsUpdatePayload {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  suffix?: string;
  dateOfBirth?: string;
  sex?: string;
  email?: string;
  primaryProviderId?: number;
  address?: AddressInfo;
  contactInfo?: ContactInfo;
  race?: string;
  ethnicity?: string;
  language?: string;
  maritalStatus?: string;
  occupation?: string | number;
  genderIdentity?: string | number;
  sexualOrientation?: string;
  preferredPronouns?: string;
  assignedSexAtBirth?: string;
  preferredName?: string;
  firstNameUsed?: string;
  emergencyContact?: EmergencyContact;
  communicationPreference?: CommunicationPreference;
  driverLicense?: DriverLicenseInfo;
  billingInfo?: BillingInfo;
}

export async function getDemographics(appointmentId: string): Promise<DemographicsResponse> {
  return jsonRequest<DemographicsResponse>(
    `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/demographics`,
    {
      method: "GET",
    }
  );
}

export async function updateDemographics(
  appointmentId: string,
  payload: DemographicsUpdatePayload
): Promise<void> {
  await jsonRequest<void>(
    `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/demographics`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    }
  );
}

export async function getMasterData(types: string): Promise<any> {
  const query = encodeURIComponent(types);
  return jsonRequest<any>(
    `/api/athena/digital-checkin/master-data?types=${query}`,
    {
      method: "GET",
    }
  );
}

// Pharmacy APIs

export interface DefaultPharmacy {
  zip?: string;
  clinicalprovidername?: string;
  city?: string;
  phonenumber?: string;
  receivertype?: string;
  clinicalproviderid?: number;
  pharmacytype?: string;
  acceptfax?: boolean;
  state?: string;
  faxnumber?: string;
  address1?: string;
}

export interface PharmacySearchItem {
  zip?: string;
  name?: string;
  city?: string;
  pharmacytype?: string;
  fax?: string;
  phone?: string;
  clinicalprovidernpi?: number;
  distance?: string;
  address?: string;
  clinicalproviderid?: string | number;
  ncpdpid?: string;
  state?: string;
}

export interface PharmacySearchResponse {
  clinicalproviders?: PharmacySearchItem[];
  totalcount?: number;
}

export async function getDefaultPharmacy(
  patientId: string | number,
  departmentId: string | number
): Promise<DefaultPharmacy | null> {
  const path = `/api/athena/digital-checkin/patient/${encodeURIComponent(
    String(patientId)
  )}/default-pharmacy?departmentId=${encodeURIComponent(String(departmentId))}`;

  try {
    const response = await axiosInstance.get(path, {
      headers: { client: 'axis', domain: 'axis' }
    });
    return response.data as DefaultPharmacy;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No default pharmacy on file yet; treat as "none" without error.
      return null;
    }
    const text = typeof error.response?.data === 'string' 
      ? error.response.data 
      : JSON.stringify(error.response?.data || {});
    throw new Error(text || error.response?.statusText || "Request failed");
  }
}

export async function searchPharmacies(params: {
  name: string;
  zip?: string;
  city?: string;
  state?: string;
}): Promise<PharmacySearchResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("name", params.name);
  if (params.zip) searchParams.set("zip", params.zip);
  if (params.city) searchParams.set("city", params.city);
  if (params.state) searchParams.set("state", params.state);

  return jsonRequest<PharmacySearchResponse>(
    `/api/athena/digital-checkin/pharmacies/search?${searchParams.toString()}`,
    {
      method: "GET",
    }
  );
}

export async function updateDefaultPharmacy(
  patientId: string | number,
  body: { departmentId: string | number; clinicalProviderId: string | number }
): Promise<void> {
  await jsonRequest<void>(
    `/api/athena/digital-checkin/patient/${encodeURIComponent(
      String(patientId)
    )}/default-pharmacy`,
    {
      method: "PUT",
      body: JSON.stringify({
        departmentId: body.departmentId,
        clinicalProviderId: body.clinicalProviderId,
      }),
    }
  );
}

// Allergy APIs

export interface AllergySearchItem {
  allergyid?: string | number;
  allergy?: string;
  allergenid: string | number;
  allergenname: string;
}

export type AllergySearchResponse = AllergySearchItem[];

export async function searchAllergies(searchValue: string): Promise<AllergySearchResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("searchvalue", searchValue);

  const response = await jsonRequest<AllergySearchResponse>(
    `/api/athena/digital-checkin/reference/allergies?${searchParams.toString()}`,
    {
      method: "GET",
    }
  );
  
  // API returns array directly, ensure it's an array
  return Array.isArray(response) ? response : [];
}

// Medication APIs

export interface MedicationSearchItem {
  medication: string;
  medicationid: string | number;
}

export type MedicationSearchResponse = MedicationSearchItem[];

export async function searchMedications(searchValue: string): Promise<MedicationSearchResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("searchvalue", searchValue);

  const response = await jsonRequest<MedicationSearchResponse>(
    `/api/athena/digital-checkin/reference/medications?${searchParams.toString()}`,
    {
      method: "GET",
    }
  );
  
  // API returns array directly, ensure it's an array
  return Array.isArray(response) ? response : [];
}

// Copay APIs

export interface CopayResponse {
  appointmentId: string;
  copay: number;
}

export interface PaymentInitiateResponse {
  appointmentId: string;
  copay: number;
  status: string;
  message: string;
  // Payabli payment fields
  payabliToken?: string;
  payabliEntryId?: string;
  paymentId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  invoiceNumber?: string;
}

export async function getCopay(appointmentId: string): Promise<CopayResponse> {
  return jsonRequest<CopayResponse>(
    `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/copay`,
    {
      method: "GET",
    }
  );
}

export async function initiateCopayPayment(appointmentId: string): Promise<PaymentInitiateResponse> {
  return jsonRequest<PaymentInitiateResponse>(
    `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/payment/copay/initiate`,
    {
      method: "POST",
    }
  );
}

// Workflow API

export enum WorkflowStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  SYNCED = 'SYNCED',
}

export enum WorkflowType {
  DEMOGRAPHIC = 'DEMOGRAPHIC',
  HEALTH_HISTORY_FORM = 'HEALTH_HISTORY_FORM',
  SCREENERS = 'SCREENERS',
  CONSENT_FORM = 'CONSENT_FORM',
}

export enum StepStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
  SYNCED = 'SYNCED',
}

export interface WorkflowStepInfo {
  id: string;
  workflowType: WorkflowType;
  stepName: string;
  displayOrder: number;
  entityId: string | null;
  status: StepStatus;
  startedAt: string | null;
  completedAt: string | null;
  metadata: string | null;
  createdAt: string;
  updatedAt: string;
  stepUrl: string;
}

export interface DigitalCheckinWorkflowResponse {
  id: string;
  appointmentId: number;
  status: WorkflowStatus;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  steps: WorkflowStepInfo[];
}

export async function getWorkflow(appointmentId: string): Promise<DigitalCheckinWorkflowResponse> {
  return jsonRequest<DigitalCheckinWorkflowResponse>(
    `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/workflow`,
    {
      method: "GET",
    }
  );
}

// Health history, screeners, and consent forms can be wired as needed
// once their payload mapping is finalized.

